import type { ContactFollowUp } from "@/features/contact/types";
import type {
  FollowUpConversionType,
  FollowUpCreditOffer,
  FollowUpCreditRecord,
  FollowUpHistoryCredit,
} from "@/features/follow-up/types";

import { useMockStore } from "@/mock/store/mockStore";

/** Detaches made in this session; the seed has no credits of its own. */
const detached = new Map<string, string | null>();

/** Credits added from the drawer in this session. */
const attached = new Map<string, ContactFollowUp>();

/** Follow-ups marked done in this session, by contact: the seed keeps no history. */
const completed = new Map<string, ContactFollowUp>();

export const rememberCompletedFollowUpMock = (
  contactId: string,
  followUp: ContactFollowUp,
) => {
  completed.set(contactId, {
    ...followUp,
    closedAt: new Date().toISOString(),
    outcome: "completed",
    closedBySource: "user",
    closedByUserId: useMockStore.getState().currentUserId,
  });
};

const offeredFollowUp = (contactId: string): ContactFollowUp | null => {
  const contact = useMockStore
    .getState()
    .contacts.find((row) => row.id === contactId);

  return contact?.followUp ?? completed.get(contactId) ?? null;
};

const key = (conversionType: FollowUpConversionType, conversionId: string) =>
  `${conversionType}:${conversionId}`;

/**
 * The contact's first booking reads as credited at booking to their open
 * follow-up, which is what the form does for real; their later bookings stay
 * uncredited so the drawer has something to offer. Orders carry no credit here.
 */
export const getFollowUpCreditMock = (
  conversionType: FollowUpConversionType,
  conversionId: string,
): FollowUpCreditRecord | null => {
  if (conversionType !== "appointment") return null;

  const { appointments } = useMockStore.getState();
  const appointment = appointments.find((row) => row.id === conversionId);

  if (!appointment?.contactId) return null;

  const added = attached.get(key(conversionType, conversionId));
  const isFirstForContact =
    appointments.find((row) => row.contactId === appointment.contactId)?.id ===
    conversionId;
  const followUp =
    added ??
    (isFirstForContact ? offeredFollowUp(appointment.contactId) : null);

  if (!followUp) return null;

  const detachedBy = detached.get(key(conversionType, conversionId));

  return {
    followUpId: followUp.id,
    creditedByUserId: useMockStore.getState().currentUserId ?? null,
    creditedAtConversion: !added,
    followUpReason: followUp.reason,
    setAt: followUp.createdAt ?? appointment.createdAt,
    setBySource: followUp.source,
    setByUserId: followUp.createdByUserId ?? null,
    doneAt: followUp.closedAt ?? null,
    doneByUserId: followUp.closedByUserId ?? null,
    repliedAt: null,
    detachedAt: detachedBy === undefined ? null : appointment.createdAt,
    detachedByUserId: detachedBy ?? null,
  };
};

export const setFollowUpCreditDetachedMock = (
  conversionType: FollowUpConversionType,
  conversionId: string,
  isDetached: boolean,
): FollowUpCreditRecord | null => {
  const id = key(conversionType, conversionId);

  if (isDetached)
    detached.set(id, useMockStore.getState().users[0]?.id ?? null);
  else detached.delete(id);

  return getFollowUpCreditMock(conversionType, conversionId);
};

/** An open follow-up, or one marked done in this session, offered and ticked. */
export const getFollowUpCreditOfferMock = (
  contactId: string,
  at?: string | null,
): FollowUpCreditOffer => {
  const followUp = offeredFollowUp(contactId);
  const setAfter = Boolean(
    at && followUp?.createdAt && followUp.createdAt > at,
  );

  if (!followUp || setAfter) {
    return { followUp: null, suggested: false, verdict: "none" };
  }

  return {
    followUp: {
      id: followUp.id,
      reason: followUp.reason,
      setAt: followUp.createdAt ?? new Date().toISOString(),
      setBySource: followUp.source,
      setByUserId: followUp.createdByUserId ?? null,
      doneAt: followUp.closedAt ?? null,
      doneByUserId: followUp.closedByUserId ?? null,
    },
    suggested: true,
    verdict: followUp.closedAt ? "chase_held" : "open",
  };
};

export const attachFollowUpCreditMock = (
  conversionType: FollowUpConversionType,
  conversionId: string,
  followUpId: string,
): FollowUpCreditRecord => {
  const appointment = useMockStore
    .getState()
    .appointments.find((row) => row.id === conversionId);
  const followUp = appointment?.contactId
    ? offeredFollowUp(appointment.contactId)
    : null;

  if (!followUp || followUp.id !== followUpId) {
    throw new Error("Not one of this contact's follow-ups");
  }

  const id = key(conversionType, conversionId);

  attached.set(id, followUp);
  detached.delete(id);

  return getFollowUpCreditMock(conversionType, conversionId)!;
};

/** What the contact's follow-up led to, as the history endpoint would list it. */
export const followUpCreditsForMock = (
  contactId: string,
): FollowUpHistoryCredit[] =>
  useMockStore
    .getState()
    .appointments.filter((row) => row.contactId === contactId)
    .flatMap((row) => {
      const record = getFollowUpCreditMock("appointment", row.id);

      return record && !record.detachedAt
        ? [
            {
              followUpId: record.followUpId,
              conversionType: "appointment" as const,
              conversionId: row.id,
              creditedByUserId: record.creditedByUserId,
              creditedAtConversion: record.creditedAtConversion,
              repliedAt: record.repliedAt,
            },
          ]
        : [];
    });

/** The contact's follow-ups as the history endpoint lists them: the open one and the one marked done this session, each with what it led to. */
export const followUpHistoryMock = (contactId: string): ContactFollowUp[] => {
  const contact = useMockStore
    .getState()
    .contacts.find((row) => row.id === contactId);
  const rows = [contact?.followUp, completed.get(contactId)].filter(
    (row): row is ContactFollowUp => !!row,
  );
  const credits = followUpCreditsForMock(contactId);

  return rows.map((row) => ({
    ...row,
    credits: credits.filter((credit) => credit.followUpId === row.id),
  }));
};
